import { Dispatch, SetStateAction, useRef, useState } from "react";
import TextInput from "../shared/inputs/TextInput";
import Dropdown, { SingleStringSelectChangeValue } from "../shared/inputs/Dropdown";
import { categories } from "../../assets/categories";
import { CreateCollectionForm } from "./CreateListModal";
import SwitchOptionButton from "../shared/inputs/SwitchOptionButton";
import { InfoIcon, UploadImageIcon } from "../../assets/icons";
import { fallbackImage } from "../../constants/urls";
import usePreviewImage from "../../hooks/usePreviewImage";
import { Categories } from "../../types/SharedTypes";
import MediaSelectorModal from "../shared/modals/MediaSelectorModal";

type Props = {
  form: CreateCollectionForm;
  setForm: Dispatch<SetStateAction<CreateCollectionForm>>;
  setNewImage: (file?: File) => void;
  isEdit?: boolean;
  isSet?: boolean;
  isCurrent?: boolean;
  collectionId?: string;
}

const CreateCollectionTab = ({form, setForm, setNewImage, isEdit, collectionId, isSet, isCurrent}: Props) => {

  const currentCategory = categories.find(opt => opt.value === form.category) || null;
  const {clearImage: clearPreviewImage, handleImage, imagePreview} = usePreviewImage(setNewImage);
  const originalImageRef = useRef<string|undefined>(form.image ? form.image : undefined);

  const [showMediaModal, setShowMediaModal] = useState(false);
  
  const updateCategory = (data: SingleStringSelectChangeValue) => {
    if (!data) return;

    setForm((prev) => ({
      ...prev,
      category: data.value as Categories,
    }));
  };

  const clearImage = () => {
    if (!imageHasChanged) return;
    if (imagePreview) clearPreviewImage();
    else setForm(prev => ({...prev, image: originalImageRef.current}));
  }

  const imageHasChanged = !!imagePreview || originalImageRef.current !== form.image;
  const isSingular = (isSet && isCurrent) || !isSet;

  const CategoryOption = () => {
    return (
      <div className="item-list med">
        <label aria-required={true} className="subtitle-medium indent">{"Category"}</label>
        <Dropdown
          value={currentCategory}
          options={categories}
          onChange={updateCategory}
          error={false}
          placeholder={'Select category...'}
          openToTop
          isSearchable={false}
        />
      </div>
    )
  }

  const VisibilityOption = () => {
    return (
      <SwitchOptionButton
        label={"Visibility"}
        required
        options={[
          {
            text: "Public",
            onClick: () => setForm(prev => ({...prev, isPublic: true})),
            selected: form.isPublic
          },
          {
            text: "Personal",
            onClick: () => setForm(prev => ({...prev, isPublic: false})),
            selected: !form.isPublic
          }
        ]} 
      />
    )
  }

  const InfoBar = () => {
    return (
      <div className="item-row tile padding-small center-vertical" data-small={true}>
        <div className="icon-display secondary small">
          <InfoIcon />
        </div>
        <p className="subtitle-large spaced">
          {!isSet && <>A collection is a group of lists. <span className="italic secondary">"My favourite movies by genre."</span></>}
          {isSet && !isEdit && 'Create the first collection in your series.'}
          {isSet && isEdit && isCurrent && 'Changes to these settings will effect only the current period collection.'}
          {isSet && isEdit && !isCurrent && 'Changes to these settings will effect all collections in this smart collection set.'}
        </p>
      </div>
    )
  }

  return (
    <>
      {showMediaModal &&
        <MediaSelectorModal
          postId={collectionId}
          imagePreview={imagePreview}
          clearPreviewImage={clearPreviewImage}
          originalImage={originalImageRef.current}
          currentImage={form.image}
          setNewImage={(image) => setForm(prev => ({...prev, image}))}
          setModalVisible={setShowMediaModal}
          handleImage={handleImage}
          closeOnSelect
        />
      }
      <section className="padding">
        <div className='item-list large'>
          {(!isEdit || isSet) && <InfoBar />}

          <div className={(isSet && isCurrent || !isSet) ? "left-item-layout large" : ""}>
            {isSingular && 
              <ThumbnailOption
                imageSrc={imagePreview || form.image || fallbackImage}
                imagePreview={imagePreview}
                clearImage={clearImage}
                setShowMediaModal={setShowMediaModal}
              />
            }
            <div className="item-list">
              {!isCurrent &&
                <TextInput
                  value={form.title}
                  onChange={(val) => setForm(prev => ({...prev, title: val}))}
                  label='title'
                  id='titleinput'
                  required
                />
              }
              {isSingular && 
                <TextInput
                  value={form.description}
                  onChange={(val) => setForm(prev => ({...prev, description: val}))}
                  label='description'
                  id='descriptioninput'
                  required={false}
                />
              }
            </div>
          </div>
          
          {!isCurrent && 
            <>
              <VisibilityOption />
              <CategoryOption />
            </>
          }
        </div>
      </section>
    </>
  )
};

type ThumbnailOptionProps = {
  setShowMediaModal: (prev: boolean) => void;
  imageSrc: string;
  imagePreview?: string;
  clearImage: () => void;
  hideLabel?: boolean;
  disabled?: boolean;
}

export const ThumbnailOption = ({setShowMediaModal, imageSrc, imagePreview, clearImage, hideLabel, disabled}: ThumbnailOptionProps) => {
  return (
    <div className="item-list">
      {!hideLabel && <p className="subtitle-medium indent">Thumbnail</p>}
      <button aria-disabled={disabled} className="empty" onClick={() => disabled ? null : setShowMediaModal(true)}>
        <div className="item-list center-vertical center-horizontal position-relative">
          <div className='position-absolute icon-display large'>
            <UploadImageIcon />
          </div>
          <div className='thumb large'>
            <img src={imageSrc} />
          </div>
        </div>
      </button>
      {imagePreview && 
        <button className="subtle tiny" onClick={clearImage}>
          Remove
        </button>
      }
    </div>
  )
}

export default CreateCollectionTab;